import { useEffect, useMemo, useState } from "react"

import { useLazyGetContractorUsersQuery } from "store/api/contractorsApi"
import { ApiTypes } from "types"
import styles from "assets/scss/index.module.scss"

import { convertContractorUsersToTableRows } from "utils/convertData"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
// import { CheckboxNew } from "components/form/CheckboxNew"
import TableWrapper from "components/table/TableWrapper"

import AddContractorUserModal from "./AddContractorUserModal"
import EditContractorUserModal from "./EditContractorUserModal"
import { getContractorUsersTableHeaderCells } from "./getContractorUsersTableHeaderCells"

export const ContractorUsers = () => {
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<null | ApiTypes.Model.User>(null)
  const [showDisabled, setShowDisabled] = useState(false)

  const [triggerGetUsers, { isFetching, data }] = useLazyGetContractorUsersQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const filteredTableHeaderCells = useMemo(() => getContractorUsersTableHeaderCells(showDisabled), [showDisabled])

  const requestParams = useMemo(() => {
    if (showDisabled) {
      return { ...urlQueryParams, is_enabled: "0" }
    }
    return urlQueryParams
  }, [urlQueryParams, showDisabled])

  useEffect(() => {
    triggerGetUsers(requestParams)
  }, [requestParams])

  const onAddClick = () => {
    setShowAddUserModal(true)
  }

  const onEditClick = async (id: number) => {
    const user = data?.data.find(user => user.id === id)
    if (user) {
      setSelectedUser(user)
      setShowEditUserModal(true)
    }
  }

  const handleShowDisabled = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowDisabled(event?.currentTarget?.checked)
    if (event?.currentTarget?.checked) {
      setUrlQueryParams({
        is_enabled: null,
      })
    }
  }

  const { handleExport } = useExportCSV({
    params: requestParams,
    headerCells: filteredTableHeaderCells,
    meta: data?.meta,
    fileName: "contractor_users",
    convertData: convertContractorUsersToTableRows,
    useLazyQuery: useLazyGetContractorUsersQuery,
  })

  const handleAddEditUserSuccess = () => {
    setShowAddUserModal(false)
    setShowEditUserModal(false)
    setSelectedUser(null)
  }

  return (
    <>
      <ContentWrapper title="Users list">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={onAddClick} color="blue">
              Add user
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <CheckboxNew
              className={styles.tableNavItem}
              isReverse={true}
              id="showDisabled"
              isChecked={showDisabled}
              onChange={handleShowDisabled}
            >
              Show Disabled
            </CheckboxNew>
            <ButtonNew onClick={handleExport} className={styles.tableNavItem} disabled={data?.meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          headerCells={filteredTableHeaderCells}
          onEditClick={onEditClick}
          isLoading={isFetching}
          data={convertContractorUsersToTableRows(data?.data)}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showAddUserModal && (
        <AddContractorUserModal onCancel={() => setShowAddUserModal(false)} onSuccess={handleAddEditUserSuccess} />
      )}
      {showEditUserModal && selectedUser && (
        <EditContractorUserModal
          onCancel={() => {
            setShowEditUserModal(false)
            setSelectedUser(null)
          }}
          onSuccess={handleAddEditUserSuccess}
          user={{
            id: selectedUser.id,
            fullName: selectedUser.full_name,
            email: selectedUser.email,
            phone: selectedUser.phone,
            isDisable: selectedUser.is_enabled === "0",
            userRole: selectedUser.role.id,
          }}
        />
      )}
    </>
  )
}
